import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import JourneyFrame from "@/components/JourneyFrame/JourneyFrame";
import toast from "@/lib/toast";
import { getCurrentUser, saveCurrentUser } from "@/lib/utils";
import { useSignupJourney } from "@/contexts/SignupJourneyContext";
import {
  AUTH_ROUTE_PATHS,
  JOURNEY_STEP_KEYS,
  getSignUpPath,
  resolveAuthRole,
} from "@/pages/Auth/authConfig";
import {
  getSignUpJourneyDraft,
  saveSignUpJourneyDraft,
} from "@/pages/Auth/signUpJourney";
import DateRangeCalendar from "@/components/DateRangeCalendar/DateRangeCalendar";
import { useQuestionnaireBackNavigation } from "@/hooks/useQuestionnaireBackNavigation";
import QuestionnaireService from "@/services/api/questionnaire";
import { useAppDispatch } from "@/store/hooks";
import { fetchUserProfile } from "@/store/slices/profileSlice";
import type { OnboardingState, QuestionOption } from "@/types/question";
import { isBlockedStatus, isUnderReview } from "@/types/userStatus";
import styles from "./QuestionnaireManagement.module.scss";
const FREE_TEXT_TYPES = ["text", "url", "date", "range"];

const QuestionnaireManagament = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { role: roleParam } = useParams();
  const role = resolveAuthRole(roleParam);

  const {
    onboardingState,
    setOnboardingState,
    selectedAnswer,
    setSelectedAnswer,
    selectedCheckboxAnswers,
    setSelectedCheckboxAnswers,
    freeTextAnswer,
    setFreeTextAnswer,
    clearJourneyState,
  } = useSignupJourney();

  // True when user is returning to this page with previously-fetched state in context.
  // Used to skip the initial API fetch and the freeTextAnswer reset on re-mount.
  const skipFetchOnMountRef = useRef(onboardingState !== null);
  const skipFreeTextResetOnMountRef = useRef(onboardingState !== null);

  // Don't show a loading spinner if we already have cached questionnaire state.
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(
    onboardingState === null,
  );
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const signUpDraft = role ? getSignUpJourneyDraft(role) : null;

  const isQuestionnaireActive = Boolean(
    role && signUpDraft?.accountCreated && !signUpDraft?.questionsCompleted,
  );

  const { canGoBack, goToPreviousQuestion, pushSnapshot } =
    useQuestionnaireBackNavigation({
      enabled: isQuestionnaireActive,
      onboardingState,
      selectedAnswer,
      selectedCheckboxAnswers,
      freeTextAnswer,
      setOnboardingState,
      setSelectedAnswer,
      setSelectedCheckboxAnswers,
      setFreeTextAnswer,
      skipFreeTextResetRef: skipFreeTextResetOnMountRef,
    });

  const fetchOnboardingState = async (): Promise<OnboardingState | null> => {
    setIsLoadingQuestions(true);
    setLoadError(null);
    setSelectedAnswer(undefined);
    setSelectedCheckboxAnswers([]);

    try {
      const state = await QuestionnaireService.getOnboardingQuestions();
      setOnboardingState(state);
      return state;
    } catch (error: any) {
      setLoadError(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load onboarding questions.",
      );
      setOnboardingState(null);
      return null;
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  useEffect(() => {
    if (!role || !signUpDraft?.accountCreated || signUpDraft.questionsCompleted) {
      return;
    }

    // On a return visit (context already has state) skip the fetch unless the
    // user explicitly triggered a reload via the "Try again" button (reloadKey > 0).
    if (skipFetchOnMountRef.current && reloadKey === 0) {
      skipFetchOnMountRef.current = false;
      setIsLoadingQuestions(false);
      return;
    }
    skipFetchOnMountRef.current = false;

    let isActive = true;

    const load = async () => {
      setIsLoadingQuestions(true);
      setLoadError(null);
      setSelectedAnswer(undefined);
      setSelectedCheckboxAnswers([]);

      try {
        const state = await QuestionnaireService.getOnboardingQuestions();
        if (isActive) setOnboardingState(state);
      } catch (error: any) {
        if (!isActive) return;
        setLoadError(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load onboarding questions.",
        );
        setOnboardingState(null);
      } finally {
        if (isActive) setIsLoadingQuestions(false);
      }
    };

    void load();

    return () => {
      isActive = false;
    };
  }, [
    reloadKey,
    role,
    signUpDraft?.accountCreated,
    signUpDraft?.questionsCompleted,
  ]);

  const currentQuestion = onboardingState?.question ?? null;

  // Reset free-text answer when the question changes.
  // On a return visit the user's in-progress answer is preserved (skip first run).
  useEffect(() => {
    if (skipFreeTextResetOnMountRef.current) {
      skipFreeTextResetOnMountRef.current = false;
      return;
    }
    setFreeTextAnswer(currentQuestion?.fieldType === "range" ? "5" : "");
    setSelectedCheckboxAnswers([]);
  }, [currentQuestion?.id]);

  if (!role) {
    return <Navigate replace to={AUTH_ROUTE_PATHS.roleSelection} />;
  }

  if (!signUpDraft || !signUpDraft.accountCreated) {
    return <Navigate replace to={getSignUpPath(role)} />;
  }

  if (signUpDraft.questionsCompleted) {
    return <Navigate replace to="/home" />;
  }

  const remainingCount = onboardingState?.remainingCount ?? 0;
  const totalQuestions = onboardingState?.totalQuestions ?? 0;
  const questionProgressPercent =
    currentQuestion && totalQuestions > 0
      ? (currentQuestion.displayOrder / totalQuestions) * 100
      : 0;
  const showContentLoader =
    isSubmittingAnswer ||
    isCompletingOnboarding ||
    (isLoadingQuestions && onboardingState !== null);
  const isFreeTextType = !!currentQuestion && FREE_TEXT_TYPES.includes(currentQuestion.fieldType);
  const isCheckboxType = currentQuestion?.fieldType === "checkbox";
  const isRadioType = currentQuestion?.fieldType === "radio";

  const completeOnboarding = async () => {
    setIsCompletingOnboarding(true);

    try {
      const profile = await toast.promise(
        dispatch(fetchUserProfile()).unwrap(),
        {
          loading: "Finalising your profile...",
          success: "Onboarding completed",
          error: (error) =>
            typeof error === "string"
              ? error
              : error?.message || "Failed to load your profile.",
        },
      );

      const userName =
        `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim() ||
        getCurrentUser("name") ||
        "";

      saveCurrentUser({
        id: profile?.id,
        email: profile?.email || getCurrentUser("email") || "",
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        location: profile?.location,
        mobileNo: profile?.mobileNo,
        name: userName,
        role,
        status: profile?.status ?? null,
        userName,
        userType: profile?.userType,
      });

      saveSignUpJourneyDraft({
        ...signUpDraft,
        accountCreated: true,
        questionsCompleted: true,
      });

      clearJourneyState();

      if (isUnderReview(profile?.status)) {
        navigate(AUTH_ROUTE_PATHS.waitlist);
      } else if (isBlockedStatus(profile?.status)) {
        navigate(AUTH_ROUTE_PATHS.rejected);
      } else {
        navigate("/home");
      }
    } catch {
      return;
    } finally {
      setIsCompletingOnboarding(false);
    }
  };

  const handleCheckboxToggle = (option: QuestionOption) => {
    if (!currentQuestion || isSubmittingAnswer || isCompletingOnboarding) {
      return;
    }

    setSelectedCheckboxAnswers((current) =>
      current.includes(option.id)
        ? current.filter((id) => id !== option.id)
        : [...current, option.id],
    );
  };

  const submitCheckboxAnswer = async (): Promise<boolean> => {
    if (!currentQuestion || selectedCheckboxAnswers.length === 0) return false;

    const selectedValues =
      currentQuestion.questionOptionsResponse
        ?.filter((option) => selectedCheckboxAnswers.includes(option.id))
        .map((option) => option.optionValue || option.optionLabel) ?? [];

    if (selectedValues.length === 0) return false;

    setIsSubmittingAnswer(true);

    try {
      await QuestionnaireService.submitOnboardingAnswer(currentQuestion.id, {
        answer: selectedValues,
      });
      return true;
    } catch {
      return false;
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const handleRadioSelect = (option: QuestionOption) => {
    if (!currentQuestion || isSubmittingAnswer || isCompletingOnboarding) {
      return;
    }

    setSelectedAnswer(option.id);
  };

  const submitRadioAnswer = async (): Promise<boolean> => {
    if (!currentQuestion || !selectedAnswer) return false;

    const selectedOption = currentQuestion.questionOptionsResponse?.find(
      (option) => option.id === selectedAnswer,
    );

    if (!selectedOption) return false;

    setIsSubmittingAnswer(true);

    try {
      await QuestionnaireService.submitOnboardingAnswer(currentQuestion.id, {
        answer: selectedOption.optionValue || selectedOption.optionLabel,
        questionOptionId: selectedOption.id,
      });
      return true;
    } catch {
      return false;
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const submitFreeTextAnswer = async (): Promise<boolean> => {
    if (!currentQuestion || !freeTextAnswer.trim()) return false;

    setIsSubmittingAnswer(true);

    try {
      await QuestionnaireService.submitOnboardingAnswer(currentQuestion.id, {
        answer: freeTextAnswer.trim(),
      });
      return true;
    } catch {
      return false;
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const onNext = async () => {
    if (loadError) {
      setReloadKey((current) => current + 1);
      return;
    }

    if (isLoadingQuestions || isSubmittingAnswer || isCompletingOnboarding) {
      return;
    }

    if (!currentQuestion) {
      await completeOnboarding();
      return;
    }

    if (isFreeTextType) {
      if (currentQuestion.fieldType !== "range" && !freeTextAnswer.trim()) return;
      const submitted = await submitFreeTextAnswer();
      if (!submitted) return;
    } else if (isCheckboxType) {
      const submitted = await submitCheckboxAnswer();
      if (!submitted) return;
    } else if (isRadioType) {
      const submitted = await submitRadioAnswer();
      if (!submitted) return;
    } else if (!selectedAnswer) {
      return;
    }

    if (currentQuestion) {
      pushSnapshot();
    }

    const nextState = await fetchOnboardingState();

    if (!nextState?.question || nextState.status === "COMPLETED") {
      await completeOnboarding();
    }
  };

  const getActionLabel = () => {
    if (loadError) return "Try again";
    if (isLoadingQuestions) return "Loading...";
    if (isSubmittingAnswer) return "Saving...";
    if (isCompletingOnboarding) return "Finishing...";
    if (!currentQuestion) return "Continue";
    return remainingCount > 0 ? "Next" : "Continue";
  };

  const hasValidAnswer =
    !currentQuestion ||
    (isFreeTextType
      ? currentQuestion.fieldType === "range" || !!freeTextAnswer.trim()
      : isCheckboxType
        ? selectedCheckboxAnswers.length > 0
        : !!selectedAnswer);

  const isActionDisabled =
    !loadError &&
    (isLoadingQuestions ||
      isSubmittingAnswer ||
      isCompletingOnboarding ||
      !hasValidAnswer);

  const renderQuestionInput = () => {
    if (!currentQuestion) return null;

    const { fieldType, questionOptionsResponse } = currentQuestion;
    const disabled = isSubmittingAnswer || isCompletingOnboarding;

    if (fieldType === "checkbox") {
      return (
        <>
          <div className={styles.optionStack}>
            {questionOptionsResponse?.map((option) => (
              <button
                key={option.id}
                className={`${styles.optionCard} ${selectedCheckboxAnswers.includes(option.id)
                  ? styles.optionCardActive
                  : ""
                  }`}
                disabled={disabled}
                onClick={() => handleCheckboxToggle(option)}
                type="button"
              >
                {option.optionLabel}
              </button>
            ))}
          </div>

          {!questionOptionsResponse?.length ? (
            <p className={styles.errorText}>
              No answer options are available for this question.
            </p>
          ) : (
            <p className={styles.helperText}>Select all that apply.</p>
          )}
        </>
      );
    }

    if (fieldType === "radio") {
      return (
        <>
          <div className={styles.optionStack}>
            {questionOptionsResponse?.map((option) => (
              <button
                key={option.id}
                className={`${styles.optionCard} ${selectedAnswer === option.id ? styles.optionCardActive : ""
                  }`}
                disabled={disabled}
                onClick={() => handleRadioSelect(option)}
                type="button"
              >
                {option.optionLabel}
              </button>
            ))}
          </div>

          {!questionOptionsResponse?.length ? (
            <p className={styles.errorText}>
              No answer options are available for this question.
            </p>
          ) : null}
        </>
      );
    }

    if (fieldType === "range") {
      const numVal = Number(freeTextAnswer) || 5;
      return (
        <div className={styles.rangeWrapper}>
          <div className={styles.rangeTrack}>
            <input
              className={styles.rangeInput}
              disabled={disabled}
              max={10}
              min={1}
              onChange={(e) => setFreeTextAnswer(e.target.value)}
              type="range"
              value={freeTextAnswer || "5"}
              style={
                {
                  "--val": freeTextAnswer || "5",
                } as React.CSSProperties
              }
            />
          </div>
          <div className={styles.rangeLabels}>
            <span>1</span>
            <span className={styles.rangeValue}>{numVal}</span>
            <span>10</span>
          </div>
        </div>
      );
    }

    if (fieldType === "date") {
      return (
        <DateRangeCalendar
          disabled={disabled}
          max={new Date().toISOString().split("T")[0]}
          mode="single"
          onChange={setFreeTextAnswer}
          placeholder="dd/mm/yyyy"
          value={freeTextAnswer}
        />
      );
    }

    return (
      <input
        className={styles.textInput}
        disabled={disabled}
        onChange={(e) => setFreeTextAnswer(e.target.value)}
        placeholder={fieldType === "url" ? "Enter your answer here" : "Type your answer..."}
        type={fieldType === "url" ? "url" : "text"}
        value={freeTextAnswer}
      />
    );
  };

  return (
    <JourneyFrame
      actionDisabled={isActionDisabled}
      actionLabel={getActionLabel()}
      actionType="button"
      activeStep={JOURNEY_STEP_KEYS.questions}
      bodyClassName={styles.body}
      hideProgressLabel
      isContentLoading={showContentLoader}
      progressPercent={questionProgressPercent}
      // description={
      //   loadError
      //     ? "There was a problem loading your onboarding questions."
      //     : "Answer the questions below so we can personalize your experience."
      // }
      // eyebrow={
      //   totalQuestions > 0
      //     ? `Question ${currentQuestionNumber} of ${totalQuestions}`
      //     : "Questions"
      // }
      onAction={() => void onNext()}
      onBack={canGoBack ? goToPreviousQuestion : undefined}
      role={role}
      title={
        loadError
          ? "We couldn't load your questions"
          : currentQuestion?.label || "A few final questions before you begin"
      }
    >
      {isLoadingQuestions ? (
        <div className={styles.stateCard}>
          <p className={styles.stateCopy}>Loading onboarding questions...</p>
        </div>
      ) : loadError ? (
        <div className={styles.stateCard}>
          <p className={styles.errorText}>{loadError}</p>
          <p className={styles.stateCopy}>
            Try again to reload your onboarding questions.
          </p>
        </div>
      ) : !currentQuestion ? (
        <div className={styles.stateCard}>
          <p className={styles.stateCopy}>
            No onboarding questions are available right now. Continue to finish
            setting up your profile.
          </p>
        </div>
      ) : (
        <div className={styles.questionCard}>{renderQuestionInput()}</div>
      )}
    </JourneyFrame>
  );
};

export default QuestionnaireManagament;
